import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutPortal } from '../../../LayoutPortal';
import { LocalStorage } from '../../../utils/LocalStorage';
import { Spinner } from '../../Spinner';
import { PortalReportsApi } from '../../../api/portalReports';
import { PortalReportNav } from './PortalReportNav';
import { PortalReportRenderer } from './PortalReportRenderer';
import { PortalDateRangePicker } from './PortalDateRangePicker';
import { usePortalClientId } from '../../../hooks/usePortalClientId';
import { useReportModality } from '../../../hooks/useReportModality';
import { PortalInformesLanding } from '../informes/PortalInformesLanding';

/**
 * Convierte `view.config.period` (snake_case persistido) al shape que espera
 * `dateRangeOverride` y `PortalDateRangePicker` (camelCase).
 */
function periodToDateRange(period) {
    if (!period || typeof period !== 'object') return null;
    if (period.type === 'FIXED' && period.start_date && period.end_date) {
        return { startDate: period.start_date, endDate: period.end_date };
    }
    if (period.type === 'RELATIVE' && period.predefined_period) {
        return { predefinedPeriod: period.predefined_period };
    }
    return null;
}

/**
 * Portal del Cliente — modalidad Reportes (V1).
 *
 * Topbar tipográfico editorial + menú horizontal de reportes (si 2+) + reporte fullscreen.
 * El cliente solo puede cambiar el rango de fechas; el resto del config viene del backend.
 *
 * Props opcionales (usadas por el modo preview del agency):
 * - listFn: () => Promise — fetch del listado de reports. Default = PortalReportsApi.listMyReports.
 * - queryFn: (viewId, dateRangeOverride) => Promise — query de un report. Default = PortalReportsApi.queryMyReport.
 */

/**
 * La única puerta de `/portal/reports` — también la del preview de agencia, así el preview no
 * puede mentir. La modalidad de la agencia (`agency_report_modality`, con seed de LocalStorage)
 * elige la pantalla: Informes (app de bloques) o Reportes V1. Modalidad ausente ⇒ V1 (skew de
 * deploy: el backend y el frontend no salen juntos).
 */
export function PortalReportsLanding({ listFn, queryFn, informesListFn, informesQueryFn } = {}) {
    const { isClientReports, known } = useReportModality();
    if (!known) {
        // Primera visita sin seed: montar V1 mientras llega la modalidad dispararía su listado
        // (que en una agencia de Informes es 403) y un parpadeo de pantalla.
        return <LayoutPortal><div className="portal-editorial"><div className="portal-editorial-loading"><Spinner /></div></div></LayoutPortal>;
    }
    if (isClientReports) return <PortalInformesLanding listFn={informesListFn} queryFn={informesQueryFn} />;
    return <PortalReportsLandingV1 listFn={listFn} queryFn={queryFn} />;
}

export function PortalReportsLandingV1({ listFn, queryFn } = {}) {
    const { t } = useTranslation();
    const activeClientId = usePortalClientId();
    // Estable entre renders a propósito: `PortalReportRenderer` lleva `queryFn` en las deps
    // de su efecto de query, así que una arrow nueva por render le dispara una re-query del
    // reporte —la consulta cara— cada vez que este componente se vuelve a renderizar. Pasa
    // solo: el PortalClientContext hidrata `clients[]` después de montar, y ese cambio de
    // contexto re-renderiza esta pantalla. Solo cambia si cambia el cliente activo.
    const defaultQueryFn = useCallback(
        (viewId, dateRangeOverride) => PortalReportsApi.queryMyReport(viewId, dateRangeOverride, activeClientId),
        [activeClientId],
    );
    const effectiveQueryFn = queryFn || defaultQueryFn;
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [reports, setReports] = useState([]);
    const [activeReportId, setActiveReportId] = useState(null);
    const [dateRangeOverride, setDateRangeOverride] = useState(null);

    useEffect(() => {
        setLoading(true);
        // El error del intento anterior se limpia ACÁ: el efecto se re-dispara al cambiar de
        // cliente (multi-cliente) y sin esto el cartel quedaba pegado aunque el listado nuevo
        // respondiera 200 — sólo se recuperaba recargando la página (QA 08/09, QA-portal-04).
        setError(null);
        // Cliente activo (multi-cliente): scopea el listado y la query al espacio que el
        // usuario está mirando. En preview llega listFn/queryFn y este id no se usa.
        const fetcher = listFn || (() => PortalReportsApi.listMyReports(activeClientId));
        fetcher()
            .then((response) => {
                // La condición mira la FORMA de la respuesta, no `status`: este endpoint
                // (`GET portal/me/reports/`, y el de preview) devuelve `{reports: [...]}` PELADO,
                // sin `status`, y `APIClient` sólo inyecta esa clave cuando la respuesta no es `ok`.
                // Un `status !== 'OK'` manda todos los 200 legítimos a la pantalla de error (fue así
                // en producción el 06-07/09, card ZfUlvo6V). Con la forma se cumple igual lo que
                // pedía C-informes-56: un código tipado no vale 'ERROR' y no puede colgar el spinner.
                // Y el texto crudo del backend no va a pantalla.
                if (!response || !Array.isArray(response.reports)) {
                    setError(t('portal_editorial.access_error'));
                    setLoading(false);
                    return;
                }
                const list = response.reports;
                setReports(list);
                if (list.length > 0) setActiveReportId(list[0].id);
                setLoading(false);
            })
            .catch(() => {
                setError(t('portal_editorial.access_error'));
                setLoading(false);
            });
    }, [t, listFn, activeClientId]);

    const activeReport = useMemo(
        () => reports.find((r) => r.id === activeReportId) || null,
        [reports, activeReportId]
    );

    // Período de comparación del config (solo lectura para el cliente). Si la vista
    // se guardó con compare=true, lo mostramos como sub-label "vs <período>" para que
    // el cliente entienda contra qué se están midiendo los deltas.
    const compareValue = useMemo(() => {
        if (!activeReport?.config?.compare) return null;
        return periodToDateRange(activeReport.config.compare_period);
    }, [activeReport]);

    // Sincroniza el rango por default con el período guardado en la vista (config.period).
    // Sin esto, el picker mostraba "Últimos 30 días" hardcoded mientras la query usaba
    // el período real del config — UI mintiendo sobre los datos.
    useEffect(() => {
        if (!activeReport) return;
        setDateRangeOverride(periodToDateRange(activeReport.config?.period));
    }, [activeReport]);

    const agencyName = LocalStorage.fetchAgencyDisplayName ? LocalStorage.fetchAgencyDisplayName() : '';
    const clientName = (LocalStorage.fetchUserFullName && LocalStorage.fetchUserFullName()) || '';

    const renderEmpty = () => (
        <div className="portal-editorial-empty">
            <span className="portal-editorial-eyebrow">{t('portal_editorial.empty_eyebrow')}</span>
            <h2 className="portal-editorial-empty-title">{t('portal_editorial.empty_title')}</h2>
            <p className="portal-editorial-empty-body">{t('portal_editorial.empty_body', { agency: agencyName || 'Tu agencia' })}</p>
        </div>
    );

    const renderError = () => (
        <div className="portal-editorial-empty">
            <span className="portal-editorial-eyebrow -error">{t('portal_editorial.error_eyebrow')}</span>
            <h2 className="portal-editorial-empty-title">{t('portal_editorial.error_title')}</h2>
            <p className="portal-editorial-empty-body">{error}</p>
        </div>
    );

    const datePicker = (
        <PortalDateRangePicker
            value={dateRangeOverride}
            compareValue={compareValue}
            onChange={setDateRangeOverride}
        />
    );

    const showStateHeader = loading || error || (!activeReport && reports.length === 0);

    return (
        <LayoutPortal>
            <div className="portal-editorial" data-testid="portal-reports-landing-v1">
                {/* Sin nombre que encabezar (el preview no lo tiene), el eyebrow de sección
                    queda suelto arriba y compite con el que ya trae el estado vacío/error:
                    dos eyebrows en la misma pantalla (QA 08/09, QA-VIS-portal-03). */}
                {showStateHeader && clientName && (
                    <header className="portal-editorial-header -minimal">
                        <div className="portal-editorial-header-text">
                            <span className="portal-editorial-eyebrow">
                                {t('portal_editorial.section_eyebrow')}
                            </span>
                            {clientName && (
                                <h1 className="portal-editorial-title">{clientName}</h1>
                            )}
                        </div>
                    </header>
                )}

                {reports.length > 1 && (
                    <PortalReportNav
                        reports={reports}
                        activeReportId={activeReportId}
                        onSelect={setActiveReportId}
                    />
                )}

                <main className="portal-editorial-main">
                    {loading && (
                        <div className="portal-editorial-loading"><Spinner /></div>
                    )}
                    {!loading && error && renderError()}
                    {!loading && !error && reports.length === 0 && renderEmpty()}
                    {!loading && !error && activeReport && (
                        <PortalReportRenderer
                            report={activeReport}
                            dateRangeOverride={dateRangeOverride}
                            queryFn={effectiveQueryFn}
                            headerRight={datePicker}
                        />
                    )}
                </main>
            </div>
        </LayoutPortal>
    );
}
